import { parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * ISO 3166-1 alpha-2 → emoji bandera (indicadores regionales Unicode).
 */
function countryCodeToFlagEmoji(iso2) {
  if (!iso2 || typeof iso2 !== "string" || iso2.length !== 2) return null;
  const upper = iso2.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return null;
  const base = 0x1f1e6;
  return [...upper].map((c) => String.fromCodePoint(base + (c.charCodeAt(0) - 65))).join("");
}

/**
 * Dígitos del número o JID WhatsApp (sin sufijo @…).
 */
export function extractPhoneDigits(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();
  const at = s.indexOf("@");
  if (at > 0) s = s.slice(0, at);
  return s.replace(/\D/g, "");
}

/**
 * WhatsApp almacenó históricamente números mexicanos con el prefijo `1`
 * antes de la reforma de agosto 2019 (formato 521XXXXXXXXXX, 13 dígitos).
 * libphonenumber-js los rechaza porque México usa exactamente 10 dígitos
 * nacionales. Esta función normaliza al formato moderno (52XXXXXXXXXX)
 * para que el parseo pueda detectar el país correctamente.
 */
function normalizeLegacyMXNumber(digits) {
  if (digits.length === 13 && digits.startsWith("521")) {
    return "52" + digits.slice(3);
  }
  return digits;
}

/**
 * Bandera + texto para tooltip si el contacto es un número individual válido.
 * Grupos (@g.us), LID, comunidades u otros JIDs → null.
 *
 * @returns {{ flag: string, countryName: string } | null}
 * @param {string} [locale] — p. ej. `i18n.language` (`es`, `pt-BR`, …) para el nombre del país en el tooltip
 */
export function getContactCountryFlagInfo(contact, ticket, locale = "es") {
  if (!contact) return null;
  if (contact.isGroup === true) return null;
  if (ticket && ticket.isGroup === true) return null;

  const raw = contact.number;
  if (!raw || typeof raw !== "string") return null;

  const lower = raw.toLowerCase();
  // Telegram (doc 0438): `tg:<id>` es un id de usuario, no un teléfono. Sus dígitos casarían con un
  // prefijo de país al azar (tg:6234… → Indonesia).
  if (lower.startsWith("tg:")) return null;
  if (lower.includes("@g.us")) return null;
  if (lower.includes("@lid")) return null;

  const rawDigits = extractPhoneDigits(raw);
  if (rawDigits.length < 8 || rawDigits.length > 15) return null;

  // Normalizar formato legacy mexicano antes de intentar el parseo
  const digits = normalizeLegacyMXNumber(rawDigits);

  try {
    const parsed = parsePhoneNumberFromString(`+${digits}`);
    if (!parsed || !parsed.isValid()) return null;
    const country = parsed.country;
    if (!country) return null;
    const flag = countryCodeToFlagEmoji(country);
    if (!flag) return null;
    let countryName = country;
    try {
      const loc =
        typeof locale === "string" && locale.length >= 2
          ? locale.replace("_", "-").slice(0, 5)
          : "es";
      const dn = new Intl.DisplayNames([loc], { type: "region" });
      countryName = dn.of(country) || country;
    } catch {
      /* noop */
    }
    return { flag, countryName };
  } catch {
    return null;
  }
}
